import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useDeployAuth } from "../../../context/DeployAuthContext";
import ReviewStars from "./ReviewStars";

const ratings = [1, 2, 3, 4, 5];

export default function ReviewsPanel({ slug, initialReviews = [], initialSummary = {}, onReviewSaved }) {
  const { isAuthenticated } = useDeployAuth();
  const [reviews, setReviews] = useState(initialReviews);
  const [summary, setSummary] = useState(initialSummary);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(""); setSaved(false);
    try {
      const { data } = await axios.post(`/api/marketplace/listings/${encodeURIComponent(slug)}/reviews`, { rating, title, body });
      setSummary(data.summary);
      setReviews((current) => [data.review, ...current.filter((review) => review._id !== data.review._id)]);
      onReviewSaved?.(data.summary);
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Couldn't save your review.");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-gray-800 bg-off-black p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-gray-800 pb-4">
        <div>
          <h2 className="text-sm font-medium uppercase tracking-wider text-silver-dark">Community reviews</h2>
          <p className="mt-1 text-xs text-silver-dark">Ratings come from verified Limelite members.</p>
        </div>
        <ReviewStars average={summary.average} count={summary.count} />
      </div>

      {isAuthenticated ? (
        <form onSubmit={submit} className="mt-5 space-y-3 border-b border-gray-800 pb-5">
          <div className="flex items-center gap-1.5">
            <span className="mr-2 text-xs text-silver-medium">Your rating</span>
            {ratings.map((value) => (
              <button key={value} type="button" onClick={() => setRating(value)} aria-label={`${value} stars`} className={`text-xl transition-colors ${value <= rating ? "text-amber-300" : "text-gray-700 hover:text-amber-200"}`}>★</button>
            ))}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} placeholder="Title (optional)" className="w-full border border-gray-700 bg-black px-3 py-2 text-sm text-silver-light outline-none focus:border-lime-500" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} required placeholder="What was your experience?" rows={3} className="w-full resize-y border border-gray-700 bg-black px-3 py-2 text-sm text-silver-light outline-none focus:border-lime-500" />
          <div className="flex items-center gap-3">
            <button disabled={busy || !rating || !body.trim()} className="bg-lime-500 px-4 py-2 text-xs font-bold text-black disabled:opacity-40">{busy ? "Saving…" : "Publish review"}</button>
            {saved && <span className="text-xs text-lime-300">Review saved.</span>}
            {error && <span className="text-xs text-red-300">{error}</span>}
          </div>
        </form>
      ) : (
        <p className="mt-5 border-b border-gray-800 pb-5 text-sm text-silver-medium"><Link to="/limelite/deploy/login" className="text-lime-300 hover:text-lime-200">Sign in</Link> to leave a verified review.</p>
      )}

      <div className="mt-5 space-y-5">
        {reviews.length ? reviews.map((review) => (
          <article key={review._id} className="border-b border-gray-800 pb-5 last:border-0 last:pb-0">
            <div className="flex items-center justify-between gap-3"><ReviewStars average={review.rating} count={1} compact showCount={false} /><time className="text-[11px] text-silver-dark">{new Date(review.updatedAt || review.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</time></div>
            {review.title && <h3 className="mt-2 text-sm font-semibold text-silver-light">{review.title}</h3>}
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-silver-medium">{review.body}</p>
          </article>
        )) : <p className="text-sm text-silver-dark">Be the first to share what you think.</p>}
      </div>
    </section>
  );
}
